import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { NavLink } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import EmployeeModal from './parts/employee-modal.component';
import Divider from '../../components/divider/divider.component';
import Table from '../../components/table/table.component';
import ConfirmModal from '../../components/confirm-modal/confirm-modal.component';
import { setCurrentPage } from '../../redux/entities/admin/users';
import {
  getUsers,
  deleteUsers,
  disableUsers,
  enableUsers,
} from '../../redux/thunks/admin/users';
import { useDebounce } from '../../utils/hooks/useDebounce';
import { RowButtons } from '../../components/table/table.styles';

const EmployeesPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { users, currentPage, limit, totalDocs, isUpdating, isFetching } =
    useSelector((state) => state.entities.admin.users);
  const [showAddModal, setShowAddModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToDisable, setUserToDisable] = useState(null);
  const [userToEnable, setUserToEnable] = useState(null);

  useEffect(() => {
    dispatch(
      getUsers({
        page: currentPage,
        limit,
        search: searchValue,
      })
    );
  }, [currentPage, limit, searchValue]);

  useDebounce(
    () => {
      dispatch(setCurrentPage(1));
    },
    [searchValue],
    500
  );

  const handleDelete = (ids) => {
    const onSuccess = (message) => {
      enqueueSnackbar(message, { variant: 'success' });
      setUserToDelete(null);
    };
    const onFailure = (message) =>
      enqueueSnackbar(message, { variant: 'error' });

    dispatch(deleteUsers({ ids, onSuccess, onFailure }));
  };

  const handleDisable = (ids) => {
    const onSuccess = (message) => {
      enqueueSnackbar(message, { variant: 'success' });
      setUserToDisable(null);
    };
    const onFailure = (message) =>
      enqueueSnackbar(message, { variant: 'error' });

    dispatch(disableUsers({ ids, onSuccess, onFailure }));
  };

  const handleEnable = (ids) => {
    const onSuccess = (message) => {
      enqueueSnackbar(message, { variant: 'success' });
      setUserToEnable(null);
    };
    const onFailure = (message) =>
      enqueueSnackbar(message, { variant: 'error' });

    dispatch(enableUsers({ ids, onSuccess, onFailure }));
  };

  const handleEdge = useCallback(() => {
    if (currentPage * limit < totalDocs) {
      dispatch(setCurrentPage(currentPage + 1));
    }
  }, [currentPage, limit, totalDocs]);

  return (
    <>
      {showAddModal && (
        <EmployeeModal
          show={showAddModal}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {userToEdit && (
        <EmployeeModal
          show={userToEdit}
          onClose={() => setUserToEdit(null)}
          user={userToEdit}
        />
      )}

      <ConfirmModal
        show={userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={() => handleDelete([userToDelete])}
      />

      <ConfirmModal
        show={userToDisable}
        onClose={() => setUserToDisable(null)}
        onConfirm={() => handleDisable([userToDisable])}
        confirmText={'Are you sure you want to disable this user?'}
      />

      <ConfirmModal
        show={userToEnable}
        onClose={() => setUserToEnable(null)}
        onConfirm={() => handleEnable([userToEnable])}
        confirmText={'Are you sure you want to enable this user?'}
      />

      <Table
        title='Employees'
        showAmount={true}
        amount={totalDocs}
        headerButtons={
          <>
            <Button
              variant='primary'
              onClick={() => setShowAddModal(true)}
              style={{ width: '-webkit-fill-available' }}
            >
              Add Employee
            </Button>
          </>
        }
        headerItems={[
          { label: 'Name' },
          { label: 'Email' },
          { label: 'Hourly Rate' },
          { label: 'Monthly Rate' },
          { label: '' },
        ]}
        onSearch={setSearchValue}
        onEdge={handleEdge}
        isLoading={isFetching}
      >
        {users.map((user, index) => (
          <>
            <div key={index}>
              <span
                style={{
                  ...(!user.isDisabled
                    ? {}
                    : { texspanecoration: 'line-through' }),
                }}
              >
                <NavLink to={`/hours/${user._id}/${user.email}`}>
                  {user.name}
                </NavLink>
              </span>
              <span>
                <NavLink to={`/hours/${user._id}/${user.email}`}>
                  {user.email}
                </NavLink>
              </span>
              <span>{user.hourlyRate ? `$${user.hourlyRate}` : '---'}</span>
              <span>{user.monthlyRate ? `$${user.monthlyRate}` : '---'}</span>
              <RowButtons>
                <Button
                  variant='primary'
                  size='sm'
                  onClick={() => setUserToEdit(user)}
                  disabled={isUpdating[user._id]}
                >
                  Edit
                </Button>
                {!user.isDisabled ? (
                  <Button
                    variant='danger'
                    size='sm'
                    onClick={() => setUserToDisable(user._id)}
                    disabled={isUpdating[user._id]}
                  >
                    Disable
                  </Button>
                ) : (
                  <Button
                    variant='primary'
                    size='sm'
                    onClick={() => setUserToEnable(user._id)}
                    disabled={isUpdating[user._id]}
                  >
                    Enable
                  </Button>
                )}
                <Button
                  variant='danger'
                  size='sm'
                  onClick={() => setUserToDelete(user._id)}
                  disabled={isUpdating[user._id]}
                >
                  Delete
                </Button>
              </RowButtons>
            </div>
            <Divider />
          </>
        ))}
      </Table>
    </>
  );
};

export default EmployeesPage;
